/**
 * Nyomtatható dokumentumok külön ablakban. (2.0)
 *
 * A szerződések és a fizetési felszólítások is így készülnek: a program
 * összeállítja a lapok HTML-jét, és megnyit egy önálló böngészőablakot,
 * aminek **saját stíluslapja** van. Szándékosan nem a fő felület nyomtatási
 * CSS-ére épül — egy szerződés akkor is ugyanúgy nézzen ki, ha a program
 * felülete később megváltozik.
 *
 * Az itteni CSS a közös keret: az ablak sávja, a papírlap mérete és a
 * nyomtatási szabályok. A dokumentum saját formázását (szerződés, levél)
 * a hívó adja hozzá.
 */

export const ALAP_DOKUMENTUM_CSS = `
  @page { size: A4 portrait; margin: 14mm 15mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #6b7076; font: 11pt/1.42 "Times New Roman", Georgia, serif; color: #000; }
  .sav { position: sticky; top: 0; background: #1f242a; color: #fff; padding: 9px 14px;
         display: flex; gap: 9px; align-items: center; font: 13px system-ui, sans-serif; z-index: 5; }
  .sav button { font: inherit; font-size: 12.5px; padding: 6px 13px; border: 0; cursor: pointer;
                background: #7FB8CE; color: #10161b; font-weight: 600; }
  .sav button.mas { background: #3a4048; color: #fff; }
  .sav span { margin-left: auto; opacity: .75; }
  .varakozas { padding: 40px; text-align: center; color: #5A6169; font: 14px system-ui; }
  .lapok { padding: 18px 0 40px; }
  .lap { width: 210mm; min-height: 297mm; margin: 0 auto 18px; padding: 14mm 15mm;
         background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.3); }
  @media print {
    body { background: #fff; }
    .sav { display: none; }
    .lapok { padding: 0; }
    .lap { width: auto; min-height: 0; margin: 0; padding: 0; box-shadow: none;
           page-break-after: always; }
    .lap:last-child { page-break-after: auto; }
  }
`;

/** HTML-be írható szöveg. */
export function esc(t: unknown): string {
  return String(t ?? '').replace(
    /[&<>"]/g,
    (k) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[k]!,
  );
}

/**
 * Előre megnyitott ablak.
 *
 * A böngészők csak akkor engedik a felugró ablakot, ha az **közvetlenül a
 * kattintásból** nyílik — a hálózati válasz megvárása után már nem. Ezért
 * két lépés: kattintáskor `ablakotNyit()`, majd amikor megjött az adat,
 * `dokumentumotKiir()`.
 */
export function ablakotNyit(css: string): Window | null {
  const w = window.open('', '_blank');
  if (!w) return null;
  w.document.write(
    `<!DOCTYPE html><html lang="hu"><head><meta charset="utf-8">` +
      `<title>Dokumentum készül…</title><style>${ALAP_DOKUMENTUM_CSS}${css}</style></head>` +
      `<body><div class="varakozas">A dokumentum készül…</div></body></html>`,
  );
  return w;
}

/** A teljes, önálló dokumentum HTML-je — a sávval (külön ablak) vagy anélkül (helyben). */
function teljesDokumentum(cim: string, lapok: string, css: string, savval: boolean): string {
  return (
    `<!DOCTYPE html><html lang="hu"><head><meta charset="utf-8"><title>${esc(cim)}</title>` +
    `<style>${ALAP_DOKUMENTUM_CSS}${css}</style></head><body>` +
    (savval
      ? `<div class="sav">` +
        `<button onclick="window.print()">Nyomtatás / mentés PDF-be</button>` +
        `<button class="mas" onclick="window.close()">Bezárás</button>` +
        `<span>${esc(cim)}</span></div>`
      : '') +
    `<div class="lapok">${lapok}</div></body></html>`
  );
}

/** A megnyitott ablak feltöltése a kész dokumentummal. */
export function dokumentumotKiir(w: Window, cim: string, lapok: string, css: string): void {
  w.document.open();
  w.document.write(teljesDokumentum(cim, lapok, css, true));
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
    } catch {
      /* ha a böngésző nem engedi, az sem baj */
    }
  }, 100);
}

/**
 * Tartalék: ha a böngésző letiltotta a felugró ablakot, a dokumentum **a
 * felület fölött, teljes képernyős rétegben** jelenik meg — ugyanazzal a
 * sötét sávval, nyomtatás és bezárás gombbal.
 *
 * A dokumentum egy beágyazott keretben (iframe) van, a saját stíluslapjával:
 * így a nyomtatás is a dokumentum stílusa szerint megy, nem a fő felületé,
 * és csak a dokumentum kerül papírra.
 */
export function dokumentumHelyben(cim: string, lapok: string, css: string): void {
  document.getElementById('dokhely')?.remove();

  const reteg = document.createElement('div');
  reteg.id = 'dokhely';
  reteg.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;background:#6b7076';

  const sav = document.createElement('div');
  sav.style.cssText =
    'background:#1f242a;color:#fff;padding:9px 14px;display:flex;gap:9px;align-items:center;' +
    'font:13px system-ui,sans-serif';
  const gomb = (szoveg: string, hatter: string, szin: string) => {
    const b = document.createElement('button');
    b.textContent = szoveg;
    b.style.cssText =
      `font:inherit;font-size:12.5px;padding:6px 13px;border:0;cursor:pointer;font-weight:600;` +
      `background:${hatter};color:${szin}`;
    return b;
  };
  const nyomtat = gomb('Nyomtatás / mentés PDF-be', '#7FB8CE', '#10161b');
  const bezar = gomb('Bezárás', '#3a4048', '#fff');
  const felirat = document.createElement('span');
  felirat.textContent = `${cim} · a böngésző letiltotta a külön ablakot, ezért itt jelenik meg`;
  felirat.style.cssText = 'margin-left:auto;opacity:.75';
  sav.append(nyomtat, bezar, felirat);

  const keret = document.createElement('iframe');
  keret.title = cim;
  keret.style.cssText = 'flex:1;border:0;width:100%';
  keret.srcdoc = teljesDokumentum(cim, lapok, css, false);

  const bezaras = () => {
    reteg.remove();
    document.removeEventListener('keydown', esc);
  };
  const esc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') bezaras();
  };
  nyomtat.onclick = () => keret.contentWindow?.print();
  bezar.onclick = bezaras;
  document.addEventListener('keydown', esc);

  reteg.append(sav, keret);
  document.body.appendChild(reteg);
}

/**
 * A kész dokumentum megjelenítése: az előre megnyitott ablakban, vagy ha az
 * nem nyílt meg (a böngésző letiltotta), a felület fölötti rétegben.
 */
export function dokumentumMegjelenites(w: Window | null, cim: string, lapok: string, css: string): void {
  if (w && !w.closed) dokumentumotKiir(w, cim, lapok, css);
  else dokumentumHelyben(cim, lapok, css);
}

/** Ha az adat nem jött meg, az üres ablakot ne hagyjuk ott. */
export function ablakotBezar(w: Window | null): void {
  try {
    w?.close();
  } catch {
    /* nem baj */
  }
}

/** Magyar dátum: `2026-09-08` → `2026. szeptember 8.` */
const HONAPOK = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

export function huDatum(iso: string | null | undefined): string {
  if (!iso) return '';
  const [e, h, n] = iso.split('-').map(Number);
  if (!e || !h || !n) return iso;
  return `${e}. ${HONAPOK[h - 1]} ${n}.`;
}
